<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canManagePurchaseReturns = computed(() => store.getters.hasPermission('purchase.return'));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          :title="title"
          :title1="title1"
          :items="canManagePurchaseReturns ? items : []"
        />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-input">
                <a class="btn btn-searchset">
                  <img src="../../../assets/img/icons/search-white.svg" alt="search" />
                </a>
                <div class="dataTables_filter">
                  <label>
                    <input
                      v-model="search"
                      type="search"
                      class="form-control form-control-sm"
                      placeholder="Search by return bill, supplier, reason..."
                      @input="searching"
                    />
                  </label>
                </div>
              </div>

              <div class="list-stats">
                <span class="stat-chip">Returns: <strong>{{ pagination.total }}</strong></span>
                <span class="stat-chip">Showing: <strong>{{ data.length }}</strong></span>
              </div>
            </div>

            <div class="filter-row">
              <div class="status-tabs" role="tablist" aria-label="Filter by status">
                <button
                  v-for="tab in statusTabs"
                  :key="tab.value"
                  type="button"
                  role="tab"
                  class="status-tab"
                  :class="{ active: statusFilter === tab.value }"
                  :aria-selected="statusFilter === tab.value"
                  @click="setStatusFilter(tab.value)"
                >
                  {{ tab.label }}
                </button>
              </div>
              <div class="filter-group">
                <label class="filter-label">From</label>
                <input v-model="filterFrom" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">To</label>
                <input v-model="filterTo" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <button v-if="hasFilters" class="btn-clear-filter" @click="clearFilters">Clear filters</button>
            </div>

            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading purchase returns...</span>
            </div>

            <div v-else-if="!data.length" class="empty-wrap">
              <p class="empty-title">No purchase returns found</p>
              <p class="empty-sub">{{ hasFilters ? "Try clearing the filters." : "When you send items back to a supplier, record it with “Add Purchase Return”." }}</p>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="data"
                :loading="loading"
                :pagination="paginationConfig"
                :expanded-row-keys="expandedRowKeys"
                row-key="returnid"
                @change="handleTableChange"
                @expand="handleExpand"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'billno'">
                    <span class="bill-badge">{{ record.billno }}</span>
                  </template>

                  <template v-else-if="column.key === 'status'">
                    <span class="status-pill" :class="statusClass(record.status)">{{ statusLabel(record.status) }}</span>
                  </template>

                  <template v-else-if="column.key === 'netamount'">
                    <span class="price-total">Rs. {{ formatNumber(record.netamount) }}</span>
                  </template>

                  <template v-else-if="column.key === 'totalqty'">
                    <span>{{ formatQty(record.totalqty) }}</span>
                  </template>

                  <template v-else-if="column.key === 'returndate'">
                    <span class="date-text">{{ formatDate(record.returndate) }}</span>
                  </template>

                  <template v-else-if="column.key === 'action'">
                    <div class="action-btns">
                      <button class="act-btn act-open" @click="openReturn(record.returnid)">
                        {{ canManagePurchaseReturns && record.status === "DRAFT" ? "Edit" : "View" }}
                      </button>
                      <button class="act-btn act-print" title="Print debit note" aria-label="Print debit note" @click="printReturn(record)"><i class="fa fa-print" aria-hidden="true"></i></button>
                      <button
                        v-if="canManagePurchaseReturns && record.status === 'DRAFT'"
                        class="act-btn act-confirm"
                        @click="confirmReturn(record.returnid)"
                      >
                        Confirm
                      </button>
                      <button
                        v-if="canManagePurchaseReturns && ['DRAFT', 'CONFIRMED'].includes(record.status)"
                        class="act-btn act-cancel"
                        @click="cancelReturn(record.returnid)"
                      >
                        Cancel
                      </button>
                    </div>
                  </template>
                </template>

                <template #expandedRowRender="{ record }">
                  <div class="expanded-wrap">
                    <div v-if="detailLoading[record.returnid]" class="expanded-loading">Loading return items...</div>
                    <div v-else-if="returnDetails[record.returnid]">
                      <div class="detail-header">
                        <span>Supplier: <strong>{{ returnDetails[record.returnid].vendorname || record.vendor || "-" }}</strong></span>
                        <span v-if="returnDetails[record.returnid].reason">Reason: <strong>{{ returnDetails[record.returnid].reason }}</strong></span>
                        <span v-if="returnDetails[record.returnid].remarks">Remarks: <strong>{{ returnDetails[record.returnid].remarks }}</strong></span>
                      </div>
                      <table class="detail-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Purchase Bill</th>
                            <th class="r">Qty</th>
                            <th class="r">Rate</th>
                            <th class="r">Tax</th>
                            <th>Reason</th>
                            <th class="r">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in returnDetails[record.returnid].items || []" :key="item.id">
                            <td>
                              <div class="detail-item">{{ item.itemname || item.itemid }}</div>
                              <small class="detail-sub">Batch {{ item.batchno || "-" }}</small>
                            </td>
                            <td>{{ item.purchasebillno || item.purchaseid || "-" }}</td>
                            <td class="r">{{ formatQty(item.returnqty) }}</td>
                            <td class="r">Rs. {{ formatNumber(item.purchaseprice) }}</td>
                            <td class="r">
                              Rs. {{ formatNumber(item.taxamount) }}
                              <small class="detail-sub d-block">{{ formatQty(item.taxpercent) }}% {{ item.taxtype || "GST" }}{{ item.istaxinclusive ? " incl." : "" }}</small>
                            </td>
                            <td>{{ item.reason || "-" }}</td>
                            <td class="r"><strong>Rs. {{ formatNumber(rowTotal(item)) }}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                      <div class="detail-totals">
                        <span>Taxable <strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "taxablevalue")) }}</strong></span>
                        <span>CGST <strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "cgst")) }}</strong></span>
                        <span>SGST <strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "sgst")) }}</strong></span>
                        <span v-if="sumField(returnDetails[record.returnid].items, 'igst')">IGST <strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "igst")) }}</strong></span>
                        <span class="detail-net">To recover <strong>Rs. {{ formatNumber(returnDetails[record.returnid].netamount) }}</strong></span>
                      </div>
                    </div>
                    <div v-else class="expanded-loading">No return details found.</div>
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { openPdfPreview } from "@/assets/utils/pdfPreview";
import { fetchPurchaseReturnPdf } from "@/services/invoicePdf";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  { title: "Return No.", dataIndex: "billno", key: "billno" },
  { title: "Date", dataIndex: "returndate", key: "returndate" },
  { title: "Supplier", dataIndex: "vendor", key: "vendor" },
  { title: "Items", dataIndex: "itemcount", key: "itemcount", align: "center" },
  { title: "Qty", dataIndex: "totalqty", key: "totalqty", align: "center" },
  { title: "Amount", dataIndex: "netamount", key: "netamount", align: "right" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Action", key: "action", width: 220 },
];

export default {
  name: "purchasereturnlist",
  data() {
    return {
      title: "Purchase Return List",
      title1: "Manage draft, confirmed, and cancelled purchase returns",
      items: [{ text: "Add Purchase Return", addname: "/createpurchasereturn" }],
      columns,
      statusTabs: [
        { value: "", label: "All" },
        { value: "DRAFT", label: "Draft" },
        { value: "CONFIRMED", label: "Confirmed" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
      data: [],
      loading: false,
      search: "",
      statusFilter: "",
      filterFrom: "",
      filterTo: "",
      searchTimer: null,
      expandedRowKeys: [],
      returnDetails: {},
      detailLoading: {},
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    hasFilters() {
      return Boolean(this.search || this.statusFilter || this.filterFrom || this.filterTo);
    },
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} returns`,
      };
    },
  },
  beforeMount() {
    this.getReturns();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
    },
    statusLabel(status) {
      const labels = { DRAFT: "Draft", CONFIRMED: "Confirmed", CANCELLED: "Cancelled" };
      return labels[status] || status || "-";
    },
    setStatusFilter(value) {
      if (this.statusFilter === value) return;
      this.statusFilter = value;
      this.applyFilters();
    },
    sumField(items, field) {
      return (items || []).reduce((sum, item) => sum + Number(item?.[field] || 0), 0);
    },
    rowTotal(item) {
      const amount = Number(item.amount || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : (amount + tax);
    },
    statusClass(status) {
      return {
        "pill-draft": status === "DRAFT",
        "pill-confirmed": status === "CONFIRMED",
        "pill-cancelled": status === "CANCELLED",
      };
    },
    searching() {
      this.pagination.current = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getReturns(), 300);
    },
    applyFilters() {
      this.pagination.current = 1;
      this.getReturns();
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
      this.filterFrom = "";
      this.filterTo = "";
      this.pagination.current = 1;
      this.getReturns();
    },
    async getReturns() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/purchase-returns", {
          params: {
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
            search: this.search.trim() || undefined,
            status: this.statusFilter || undefined,
            date_from: this.filterFrom || undefined,
            date_to: this.filterTo || undefined,
          },
        });
        this.data = response.data.items || [];
        this.pagination = {
          current: response.data.page || this.pagination.current,
          pageSize: response.data.page_size || this.pagination.pageSize,
          total: response.data.total || 0,
        };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchase returns." });
      } finally {
        this.loading = false;
      }
    },
    async fetchReturnDetail(returnid) {
      if (this.returnDetails[returnid] || this.detailLoading[returnid]) return;
      this.detailLoading = { ...this.detailLoading, [returnid]: true };
      try {
        const response = await axios.get(`/v1/purchase-returns/${returnid}`);
        this.returnDetails = { ...this.returnDetails, [returnid]: response.data };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load return details." });
      } finally {
        this.detailLoading = { ...this.detailLoading, [returnid]: false };
      }
    },
    handleExpand(expanded, record) {
      if (expanded) {
        this.expandedRowKeys = [...new Set([...this.expandedRowKeys, record.returnid])];
        this.fetchReturnDetail(record.returnid);
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.returnid);
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getReturns();
    },
    openReturn(returnid) {
      this.$router.push({ path: "/createpurchasereturn", query: { id: returnid } });
    },
    // The debit note PDF is generated by the server, in the same layout as the sales invoice.
    async printReturn(record) {
      if (!record?.returnid) return;
      Swal.fire({ title: "Preparing debit note...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const { blob, fileName } = await fetchPurchaseReturnPdf(record.returnid);
        Swal.close();
        openPdfPreview(blob, { title: `Debit Note ${record.billno || ""}`.trim(), fileName });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Print Error", text: "Could not load the debit note PDF." });
      }
    },
    async confirmReturn(returnid) {
      const result = await Swal.fire({
        title: "Confirm purchase return?",
        text: "This will reduce stock for the selected batches.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/purchase-returns/${returnid}/confirm`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1300, showConfirmButton: false });
        this.returnDetails = {};
        this.getReturns();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm purchase return." });
      }
    },
    async cancelReturn(returnid) {
      const result = await Swal.fire({
        title: "Cancel purchase return?",
        text: "Confirmed returns will be reversed with a new ledger entry.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/purchase-returns/${returnid}/cancel`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1300, showConfirmButton: false });
        this.returnDetails = {};
        this.getReturns();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel purchase return." });
      }
    },
  },
};
</script>

<style scoped>
.table-top { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.search-input { display:flex; align-items:center; gap:6px; }
.search-input .form-control { width:340px; height:36px; border-radius:6px; border:1px solid #e5e7eb; padding:0 12px; }
.list-stats { display:flex; gap:10px; align-items:center; }
.stat-chip { display:flex; align-items:center; gap:5px; font-size:0.82rem; color:#6b7280; background:#f9fafb; border:1px solid #e5e7eb; border-radius:20px; padding:3px 10px; }
.filter-row { display:flex; gap:10px; align-items:end; flex-wrap:wrap; margin-bottom:16px; }
.filter-group { display:flex; flex-direction:column; gap:4px; }
.filter-label { font-size:0.75rem; color:#6b7280; font-weight:600; }
.filter-input { width:150px; height:36px; border-radius:6px; border:1px solid #e5e7eb; }
.status-tabs { display:inline-flex; padding:3px; border:1px solid #e5e7eb; border-radius:8px; background:#f9fafb; align-self:end; }
.status-tab { border:0; background:transparent; padding:6px 14px; border-radius:6px; font-size:.82rem; font-weight:600; color:#6b7280; }
.status-tab.active { background:#fff; color:#c2410c; box-shadow:0 1px 3px rgba(15,23,42,.08); }
.btn-clear-filter { height:36px; padding:0 14px; border:none; border-radius:6px; background:#f3f4f6; color:#374151; font-size:0.85rem; font-weight:600; }
.loading-wrap, .empty-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:50px 20px; gap:10px; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-title { font-size:1rem; font-weight:700; color:#111827; margin:0; }
.empty-sub { font-size:0.84rem; color:#6b7280; margin:0; }
.bill-badge { display:inline-block; background:#fff7ed; color:#c2410c; border:1px solid #fdba74; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:700; width:max-content; }
.status-pill { display:inline-block; padding:2px 10px; border-radius:999px; font-size:.76rem; font-weight:700; }
.pill-draft { background:#fff7ed; color:#c2410c; }
.pill-confirmed { background:#f0fdf4; color:#15803d; }
.pill-cancelled { background:#fef2f2; color:#dc2626; }
.date-text { font-size:.82rem; color:#6b7280; }
.price-total { color:#ea580c; font-weight:700; }
.action-btns { display:flex; gap:6px; flex-wrap:wrap; }
.act-btn { border:none; border-radius:7px; padding:6px 10px; font-size:.78rem; font-weight:700; }
.act-open { background:#eff6ff; color:#2563eb; }
.act-confirm { background:#f0fdf4; color:#15803d; }
.act-cancel { background:#fef2f2; color:#dc2626; }
.act-print { background:#f0f4ff; color:#4f46e5; font-size:.82rem; }
.expanded-wrap { background:#fffaf5; border:1px solid #fed7aa; border-radius:10px; padding:14px; }
.detail-header { display:flex; gap:18px; flex-wrap:wrap; margin-bottom:10px; font-size:.82rem; color:#6b7280; }
.detail-table { width:100%; border-collapse:collapse; }
.detail-table th, .detail-table td { padding:8px 10px; border-bottom:1px solid #f3e8d8; font-size:.82rem; text-align:left; }
.detail-table th { color:#6b7280; font-weight:700; text-transform:uppercase; font-size:.72rem; }
.detail-table .r { text-align:right; }
.detail-item { color:#111827; font-weight:500; }
.detail-sub { color:#9ca3af; font-size:.72rem; }
.detail-totals { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:8px 20px; margin-top:10px; font-size:.8rem; color:#6b7280; }
.detail-totals strong { color:#111827; margin-left:4px; }
.detail-net { padding:4px 12px; border-radius:8px; background:#ffedd5; color:#c2410c; }
.detail-net strong { color:#c2410c; }
.expanded-loading { color:#6b7280; font-size:.82rem; }
</style>
